/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/member-ordering */
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { PromptInput, PromptTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import * as addPromptActions from '../../add-prompt/state/add-prompt.actions';
import { createPromptSuccessNoRouteToDetail } from '../../add-prompt/state/add-prompt.actions';
import { getRecordedFileState } from '../../add-prompt/state/add-prompt.selectors';
import { FileOption } from '../models/file.option.interface';
import { CreateOption } from '../models/prompt-create-option';
import { PromptDetailSaveEvent } from '../models/prompt-detail-form-submit-event';
import { PromptDetailView } from '../models/prompt-detail-view.model';
import { PromptPrivileges } from '../models/prompt-privileges';
import { PromptDetailFormService } from '../services/prompt-detail-form.service';
import { changePromptFile, savePromptDetailSuccess } from '../state/prompt-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-prompt-detail-view',
    styleUrls: ['./prompt-detail-view.component.scss'],
    templateUrl: './prompt-detail-view.component.html',
    standalone: false
})
export class PromptDetailViewComponent extends DetailView {
   @ViewChild('audio', { static: false })
   audioPlayer?: ElementRef<HTMLAudioElement>;

   @Input() get prompt(): PromptDetailView | null {
      return this._prompt;
   }

   set prompt(value: PromptDetailView | null) {
      this._prompt = value;
      this.originalFileId = this._prompt?.fileId ?? null;
      if (this._prompt?.typeId === PromptTypes.MusicOnHold) {
         this.isMusicOnHoldType = true;
      }
      if (value) {
         this.formService.updatePrompt(value);
      }
   }

   @Input() set promptPrivileges(privileges: PromptPrivileges) {
      this._promptsPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
      }
   }

   get promptPrivileges(): PromptPrivileges {
      return this._promptsPrivileges;
   }

   @Input() callState: CallState;

   @Input() isSystemUser: boolean;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() cancelFormSave = new EventEmitter<void>();

   @Output() emitNewFileId = new EventEmitter<number | null>();

   @Output() promptUpdatedViaCallToRecord = new EventEmitter<number | null>();

   @Output() formSubmitted = new EventEmitter<PromptDetailSaveEvent>();

   @Output() deletePrompt = new EventEmitter<{
      name: string | null;
      promptId: number;
   }>();

   form: UntypedFormGroup;

   promptTypes = Object.values(PromptTypes);

   createPromptOptionValue = 'microphone';

   recordPromptOptionValue = 'file';

   fileData;

   destinationExtension = '';

   destinationNumber = '';

   isMusicOnHoldType: boolean = false;

   newFileId: number | null;

   originalFileId: number | null;

   regexPattern = /^(\d{3})(\d{3})(\d{4})$/;

   reloadAudio = true;

   private _prompt: PromptDetailView | null;

   private _promptsPrivileges: PromptPrivileges;

   private destroyed$ = new Subject<void>();

   constructor(
      public formService: PromptDetailFormService,
      private changeDetectorRef: ChangeDetectorRef,
      private actions$: Actions,
      private store: Store
   ) {
      super();
      this.form = this.formService.form;
      this.actions$.pipe(ofType(savePromptDetailSuccess), takeUntil(this.destroyed$)).subscribe((action) => {
         if (action.data.prompt.file) {
            this._prompt = {
               ...this._prompt!,
               file: action.data.prompt.file,
               fileId: action.data.prompt.file.fileId,
            };

            this.formService.updatePrompt(this._prompt);

            this.reloadAudioPlayer();
         }
      });
      this.actions$.pipe(ofType(createPromptSuccessNoRouteToDetail), takeUntil(this.destroyed$)).subscribe((action) => {
         let updatedPrompt = this.prompt as PromptDetailView;

         if (action.promptItem.file) {
            if (updatedPrompt) {
               if (action.promptItem.file) {
                  updatedPrompt = {
                     ...updatedPrompt,
                     file: action.promptItem.file,
                     fileId: action.promptItem.file.fileId,
                  };
               }
            }
         }

         this.formService.updatePrompt(updatedPrompt);

         if (action.promptItem.file) {
            this.reloadAudioPlayer();
         }
      });

      this.actions$.pipe(ofType(addPromptActions.createFileSuccess), takeUntil(this.destroyed$)).subscribe((action) => {
         this.newFileCreated(action.fileItem ?? null);
      });
      this.actions$
         .pipe(ofType(addPromptActions.createFileFromCallToRecordSuccess), takeUntil(this.destroyed$))
         .subscribe((action) => {
            this.promptUpdatedViaCallToRecord.emit(this.originalFileId);
            this.newFileCreated(action.prompt.file ?? null);
         });

      this.store.pipe(select(getRecordedFileState)).subscribe((data) => {
         if (data?.blob) {
            this.fileData = data;
            this.changeDetectorRef.markForCheck();
         }
      });
   }

   fileNotFound = false;

   onFileNotFound() {
      this.fileNotFound = true;
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      this.newFileId = null;
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      if (this.newFileId !== null && this.originalFileId !== null) {
         this.cancelFormSave.emit();
         this.newFileId = null;
      }

      this.formSaveCanceled.emit();
   }

   newFileCreated(file: FileOption | null) {
      this.newFileId = file?.fileId ?? null;
      this.fileNotFound = false;

      this.emitNewFileId.emit(this.newFileId);

      this.form.controls['fileId'].setValue(this.newFileId);
      this.form.controls['file'].setValue(file);
      this.form.controls['fileId'].markAsDirty();
   }

   onDeleteClick(): void {
      if (this.prompt) {
         this.deletePrompt.emit({
            name: this.prompt.name,
            promptId: this.prompt.promptId,
         });
      }
   }

   promptDisplayWith(option: PromptTypes): string {
      return `${option ?? ''}`;
   }

   private getFormSubmittedEvent(): PromptDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.prompt) {
         return null;
      }
      const prompt = this.formService.getValue(this.prompt.promptId);

      let file = this.formService.getFile();

      if (this.newFileId !== null) {
         prompt.fileId = this.newFileId;
         file = null;
      }

      if (this.prompt.typeId === PromptTypes.MusicOnHold || this.prompt.typeId === PromptTypes.QueueCallback) {
         prompt.dontDeleteFileOnUpdate = true;
         prompt.copyMohFile = true;
      }

      let promptFields: (keyof PromptInput)[] = [];
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'account', 'accountId');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'typeId');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'promptId');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'isReusable');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'name');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'isActive');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'fileId');
      promptFields = this.formService.addFieldForDirtyControl(promptFields, 'promptId');

      return { file, prompt, promptFields, routeToNewPromptAfterSave: null, voiceMailGreetingPrompt: null };
   }

   changePromptFile(): void {
      if (this._prompt) {
         this.store.dispatch(
            changePromptFile({
               accountId: this._prompt.account?.accountId ?? null,
               promptId: this._prompt.promptId,
               name: this._prompt.name ?? '',
               promptType: this.form.controls['typeId'].value.typeName,
            })
         );
      }
   }

   onExtensionSelect(event) {
      this.destinationExtension = String(event.number);
      this.destinationNumber = '';
   }

   onAddDestinationNumber(event) {
      this.destinationExtension = '';
      this.destinationNumber = event;
   }

   markFormDirty() {
      this.form.markAsDirty();
   }

   displayWith(option: CreateOption) {
      return `${option ?? ''}`;
   }

   private reloadAudioPlayer(): void {
      this.reloadAudio = false;

      setTimeout(() => {
         this.reloadAudio = true;
         this.changeDetectorRef.markForCheck();
      });
   }
}
